import { FC } from 'react';
import { Checkbox, Td, Tr } from '@chakra-ui/react';
import {
  Directory,
  FileBrowserFileFragment,
  FileBrowserStorageObjectFragment,
} from '../../graphql/generated';
import { isDirectory, isFile } from '../../graphql/utils/utils';
import { FileMoveAction } from '../file-details/file-details-actions/file-move-action/file-move-action';
import { FileDownloadIconButton } from '../file-download-button/file-download-button';
import { DirectoryArchiveButton } from './directory-archive-button/directory-archive-button';
import { EnhancedFileSize } from './file-size';
import { FileStatus } from './file-status/file-status';
import { Name } from './name';
import { UpdatedAt } from './updated-at';

type FileBrowserRowProps = {
  onDownload: () => Promise<void>;
  storageObject: FileBrowserStorageObjectFragment;
  isSelectable?: boolean;
  isSelected?: boolean;
  onToggleSelection?: () => void;
};

export const FileBrowserRow: FC<FileBrowserRowProps> = ({
  storageObject,
  isSelectable = false,
  isSelected = false,
  onToggleSelection,
  ...rest
}) => {
  if (isFile(storageObject)) {
    return (
      <FileBrowserFileRow
        file={storageObject}
        isSelectable={isSelectable}
        isSelected={isSelected}
        onToggleSelection={onToggleSelection}
        {...rest}
      />
    );
  }

  if (isDirectory(storageObject)) {
    return (
      <FileBrowserDirectoryRow
        directory={storageObject}
        isSelectable={isSelectable}
        {...rest}
      />
    );
  }

  return null;
};

type FileBrowserDirectoryRowProps = {
  directory: Directory;
  isSelectable?: boolean;
};

const FileBrowserDirectoryRow: FC<FileBrowserDirectoryRowProps> = ({
  directory,
  isSelectable = false,
}) => {
  return (
    <Tr
      data-row-type="directory-row"
      key={directory.id}
      data-object-name={directory.name}
    >
      {isSelectable && (
        <Td px={4} width="60px" textAlign="right">
          {/* Directories are not selectable */}
        </Td>
      )}
      <Td colSpan={4} data-testid="file-name">
        <Name object={directory} />
      </Td>
      <Td data-testid="file-status">
        <FileStatus object={directory} />
      </Td>
      <Td data-testid="file-updated-at">
        <UpdatedAt object={directory} />
      </Td>
      <Td data-testid="file-size">
        <EnhancedFileSize object={directory} />
      </Td>
      <Td data-testid="file-actions" textAlign="right">
        {directory.canArchive && (
          <DirectoryArchiveButton directory={directory} />
        )}
      </Td>
    </Tr>
  );
};

type FileBrowserFileRowProps = {
  onDownload: () => Promise<void>;
  file: FileBrowserFileFragment;
  isSelectable?: boolean;
  isSelected?: boolean;
  onToggleSelection?: () => void;
};

const FileBrowserFileRow: FC<FileBrowserFileRowProps> = ({
  onDownload,
  file,
  isSelectable = false,
  isSelected = false,
  onToggleSelection,
}) => {
  return (
    <Tr
      data-row-type="file-row"
      key={file.id}
      data-object-name={file.name}
    >
      {isSelectable && (
        <Td px={4} width="60px" textAlign="right">
          {file.canMove && (
            <Checkbox
              isChecked={isSelected}
              onChange={onToggleSelection}
              colorScheme="blue"
              size="lg"
              aria-label={`Select ${file.name}`}
            />
          )}
        </Td>
      )}
      <Td colSpan={4} data-testid="file-name">
        <Name object={file} />
      </Td>
      <Td data-testid="file-status">
        <FileStatus object={file} />
      </Td>
      <Td data-testid="file-updated-at">
        <UpdatedAt object={file} />
      </Td>
      <Td data-testid="file-size">
        <EnhancedFileSize object={file} />
      </Td>
      <Td data-testid="file-actions" textAlign="right">
        <FileDownloadIconButton file={file} onDownload={onDownload} />
        {file.canMove && <FileMoveAction files={[file]} buttonType="IconButton" />}
      </Td>
    </Tr>
  );
};
